/* ---------------------------------------------------------------------------
   A document shown as it will print — Word's print layout.

   The document is drawn once, live, in one column the width of the printed text,
   over a stack of A4 sheets drawn behind it. Each sheet carries the running head
   and foot the paper will carry: the department and the document above, when it
   was generated and the page of how many below — the same on the period report,
   the inventory sheet and the inspection summary. Where a block or a table row
   would run over the foot of a sheet it is pushed to the top of the next one,
   and a table carried on to a new sheet has its column heads drawn again above
   it, as they print.

   THE PUSHES ARE A STYLESHEET, NOT THE TREE. A margin or a padding written onto
   the report's own elements would be taken off again by the next repaint, which
   copies every attribute from the freshly built tree (see patchElement in
   src/core/dom.ts). So each push is a rule in one <style> in the head, addressed
   by the element's place in the document, and the report's nodes are never
   touched: the caret in the summary, a select's value and the photographs
   already decoded all survive. The sheets are drawn into a slot the repaint
   leaves alone (`data-keep`).

   Everything here is for the screen. The paper's own pagination is the
   printer's, from the print rules in src/styles/app.css and the @page drawn
   here; the sheets on screen are the same page, so what is seen here is where
   it will break, near enough.
   --------------------------------------------------------------------------- */

import { h, type Child } from '../core/dom.ts';
import { mediumDate } from '../data/derive.ts';
import { agency } from '../tenant/agency.ts';

/** An A4 sheet at 96 px to the inch, 794 px wide (see .paper in src/styles/app.css). */
const PAGE_H = 1123;
/** Between two sheets on the desk. */
const GAP = 24;
const PITCH = PAGE_H + GAP;
const STYLE_ID = 'paper-pages';
const SHEETS_KEEP = 'paper-sheets';

/** What a document on paper says about itself, at the head and foot of every sheet. */
export interface PaperDocument {
  /** What it is: "Monthly progress report", "Bridge inventory sheet". */
  title: string;
  /** Of what: the structure, and for a report its period. */
  detail: string;
  /** The application it comes from, after the branch: "Construction monitoring". */
  from: string;
  /** The foot's middle — for a report, how many of its entries are in review. */
  footMid?: string;
}

/**
 * Every document's page. 10 mm above the running head and the space kept under
 * it, 16 mm below for the foot, 15 mm a side — on screen in px, and the same
 * page as `@page` takes it.
 */
const PAGE = { top: 116, bottom: 60, side: 57, headTop: 38, print: '10mm 15mm 16mm' } as const;

/** A CSS string literal. */
function cssString(text: string): string {
  return `"${text.replace(/[\\"]/g, (c) => `\\${c}`).replace(/\s+/g, ' ')}"`;
}

/**
 * The printed page: A4, the document's margins, and the foot the sheets on
 * screen carry, as the page's margin boxes — the only place a page can number
 * itself. A `<style>` drawn with the document so its strings are this
 * document's, and gone with it so nothing else prints under this foot.
 */
function printedPage(page: { print: string; footLeft: string; footMid: string }): HTMLElement {
  const foot =
    "font-family: 'Plus Jakarta Sans', 'Segoe UI', sans-serif; font-size: 9pt; color: #4a5560; " +
    'vertical-align: top; padding-top: 2mm; border-top: 0.75pt solid #1a1f24;';
  return h('style', {
    text: `@media print {
  @page {
    size: A4;
    margin: ${page.print};
    @bottom-left { ${foot} content: ${cssString(page.footLeft)}; }
    @bottom-center { ${foot} content: ${cssString(page.footMid)}; color: #8a4500; }
    @bottom-right { ${foot} content: "Page " counter(page) " of " counter(pages); text-align: right; font-family: 'JetBrains Mono', Consolas, monospace; color: #1a1f24; }
  }
}`,
  });
}

/** The page being laid out — set from the paper's own data at the start of each layout. */
let geo = { top: 0, bottom: PAGE_H, side: 0, headTop: 0 };

/** "Bridge Watch · Generated 6 Oct 2026, 14:20" — the foot's left, on every sheet. */
function generated(): string {
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return `Bridge Watch · Generated ${mediumDate(now)}, ${time}`;
}

/**
 * The head of every sheet: the department under its seal on the left, the
 * document and what it is of on the right. On paper it is fixed, which repeats
 * it on every printed page, over the space the sheet's table keeps for it; on
 * screen the sheets draw copies of it (drawSheets).
 */
function runningHead(doc: PaperDocument): HTMLElement {
  const a = agency();
  return h(
    'div',
    { class: 'runhead', 'aria-hidden': 'true', 'data-paper-head': '' },
    h(
      'div',
      { class: 'runhead__org' },
      h('div', { class: 'runhead__seal', text: a.departmentShort }),
      h(
        'div',
        {},
        h('div', { class: 'runhead__name', text: a.department }),
        h('div', { class: 'runhead__sub', text: `${a.branch} · ${doc.from}` }),
      ),
    ),
    h(
      'div',
      { class: 'runhead__doc' },
      h('div', { class: 'runhead__name', text: doc.title }),
      h('div', { class: 'runhead__detail', text: doc.detail }),
    ),
  );
}

/**
 * A document on paper: the desk, the sheets, and the document's own content
 * flowing over them at its printed measure, under the running head and over the
 * foot. The content prints as it is; the desk and the sheets are the screen's.
 */
export function paper(doc: PaperDocument, ...content: Child[]): HTMLElement {
  schedulePagination();
  const page = { ...PAGE, footLeft: generated(), footMid: doc.footMid ?? '' };
  return h(
    'div',
    { class: 'paper__desk' },
    printedPage(page),
    h(
      'div',
      {
        class: 'paper',
        'data-top': String(page.top),
        'data-bottom': String(page.bottom),
        'data-side': String(page.side),
        'data-head-top': String(page.headTop),
        'data-foot-left': page.footLeft,
        'data-foot-mid': page.footMid,
      },
      h('div', { class: 'paper__sheets', 'data-keep': SHEETS_KEEP, 'aria-hidden': 'true' }),
      h(
        'div',
        { class: 'paper__flow', style: `padding: ${page.top}px ${page.side}px 0` },
        runningHead(doc),
        /* On paper a table, so its empty head repeats at the top of every page
           and keeps the text clear of the running head fixed over it; on screen
           a plain block. */
        h(
          'table',
          { class: 'paper__table' },
          h('thead', {}, h('tr', {}, h('td', {}, h('div', { class: 'paper__gap' })))),
          h('tbody', {}, h('tr', {}, h('td', {}, ...content))),
        ),
      ),
    ),
  );
}

let scheduled = false;
let watched: Element | null = null;
let drawn = '';
const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => schedulePagination());

/** Lay the paper out again after this paint — the report calls it each time it is drawn. */
export function schedulePagination(): void {
  if (scheduled || typeof requestAnimationFrame === 'undefined') return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    paginate();
  });
}

if (typeof document !== 'undefined' && document.fonts) {
  void document.fonts.ready.then(() => schedulePagination());
}

function styleSheet(): HTMLStyleElement {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (el === null) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.append(el);
  }
  return el;
}

/** One push: an element moved down to the top of a sheet. */
interface Push {
  el: HTMLElement;
  /** A row is moved by padding its cells; anything else by its top margin. */
  row: boolean;
  /** The margin or padding it had before it was pushed. */
  base: number;
  extra: number;
}

/** Column heads drawn again on the sheet a table carries on to. */
interface Ghost {
  page: number;
  table: HTMLTableElement;
  left: number;
  top: number;
}

function paginate(): void {
  const sheet = styleSheet();
  const paper = document.querySelector<HTMLElement>('.paper');
  const flow = paper?.querySelector<HTMLElement>('.paper__flow') ?? null;
  if (paper === null || flow === null) {
    sheet.textContent = '';
    return;
  }
  const num = (k: string, d: number) => Number(paper.dataset[k] ?? d) || d;
  geo = {
    top: num('top', 0),
    bottom: PAGE_H - num('bottom', 0),
    side: num('side', 0),
    headTop: num('headTop', 0),
  };
  if (watched !== flow) {
    resized?.disconnect();
    resized?.observe(flow);
    watched = flow;
  }

  const pushes: Push[] = [];
  const ghosts: Ghost[] = [];
  const write = (pages: number) => {
    sheet.textContent = `@media screen {\n${[
      `.paper { height: ${pages * PITCH - GAP}px; }`,
      ...pushes.map((p) =>
        p.row
          ? `${pathOf(p.el, flow)} > * { padding-top: ${Math.round(p.base + p.extra)}px !important; }`
          : `${pathOf(p.el, flow)} { margin-top: ${Math.round(p.base + p.extra)}px !important; }`,
      ),
    ].join('\n')}\n}`;
  };

  /* From the report as it lays out by itself. */
  write(1);
  const origin = () => paper.getBoundingClientRect().top;

  /* How far each row has been pushed. A row is pushed by padding its cells, which
     moves its text and not its own top, so its top is read from here. */
  const shifted = new Map<Element, number>();
  for (let guard = 0; guard < 400; guard++) {
    const top0 = origin();
    const hit = firstCrossing(flow, top0, shifted);
    if (hit === null) break;
    /* Pushed before, and pushed further now: one rule, from where it already is. */
    const again = pushes.find((p) => p.el === hit.el);
    if (again) {
      const at = hit.el.getBoundingClientRect().top - top0 + (shifted.get(hit.el) ?? 0);
      if (Math.abs(hit.target - at) < 0.5) break;
      again.extra += hit.target - at;
      if (again.row) shifted.set(hit.el, again.extra);
      write(1);
      continue;
    }
    const push: Push = {
      el: hit.el,
      row: hit.row,
      base: hit.row ? padTop(hit.el) : parseFloat(getComputedStyle(hit.el).marginTop) || 0,
      extra: 0,
    };
    pushes.push(push);
    if (hit.row) {
      push.extra = hit.target - (hit.el.getBoundingClientRect().top - top0);
      shifted.set(hit.el, push.extra);
      write(1);
    } else {
      /* Margins collapse, so a margin does not always move an element by itself;
         measure, and make up the difference. */
      for (let i = 0; i < 4; i++) {
        const at = hit.el.getBoundingClientRect().top - origin();
        const short = hit.target - at;
        if (Math.abs(short) < 0.5) break;
        push.extra += short;
        write(1);
      }
    }
    if (hit.ghost) {
      ghosts.push({ page: hit.page, table: hit.ghost, left: 0, top: 0 });
    }
  }

  const bottom = flow.getBoundingClientRect().bottom - origin();
  const pages = Math.max(1, Math.floor((bottom - 1) / PITCH) + 1);
  write(pages);

  /* Where each carried-over table now is, for its heads. */
  const base = paper.getBoundingClientRect();
  for (const g of ghosts) {
    const r = g.table.getBoundingClientRect();
    g.left = r.left - base.left;
    g.top = g.page * PITCH + geo.top;
  }
  drawSheets(paper, pages, ghosts);
}

/** The first element, in reading order, that runs over the foot of its sheet or starts above its text. */
function firstCrossing(
  root: HTMLElement,
  originTop: number,
  shifted: ReadonlyMap<Element, number>,
): { el: HTMLElement; row: boolean; target: number; page: number; ghost: HTMLTableElement | null } | null {
  const visit = (parent: Element): ReturnType<typeof firstCrossing> => {
    const kids = Array.from(parent.children) as HTMLElement[];
    for (let i = 0; i < kids.length; i++) {
      const el = kids[i]!;
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.position === 'absolute' || cs.position === 'fixed') continue;
      const r = el.getBoundingClientRect();
      if (r.height === 0) continue;
      const top = r.top - originTop;
      const bottom = r.bottom - originTop;
      const page = Math.max(0, Math.floor(top / PITCH));
      const start = page * PITCH + geo.top;
      const end = page * PITCH + geo.bottom;

      if (top < start - 0.5) {
        return { el, row: cs.display === 'table-row', target: start, page, ghost: null };
      }
      if (top > end + 0.5) {
        return { el, row: cs.display === 'table-row', target: start + PITCH, page: page + 1, ghost: null };
      }
      if (bottom <= end + 0.5) continue;

      /* Over the foot. Look inside it where it can be divided. */
      if (cs.display === 'table') {
        const found = visitTable(el as HTMLTableElement, originTop, shifted);
        if (found) return found;
        continue;
      }
      if (divisible(el, cs)) {
        const found = visit(el);
        if (found) return found;
        continue;
      }
      /* Taller than a sheet's text, and already at the top of one: it runs on. */
      if (top <= start + 0.5) continue;
      return keepWithHeading(kids, i, page + 1);
    }
    return null;
  };
  return visit(root);
}

/**
 * A table over the foot of a sheet: its first row that runs over the foot of its
 * own sheet, carried to the next under its heads drawn again — or the whole table,
 * where that is its first row, so a table's heads are never left alone.
 */
function visitTable(
  table: HTMLTableElement,
  originTop: number,
  shifted: ReadonlyMap<Element, number>,
): ReturnType<typeof firstCrossing> {
  const head = table.tHead;
  const tableTop = table.getBoundingClientRect().top - originTop;
  const tablePage = Math.max(0, Math.floor(tableTop / PITCH));
  if (head && head.getBoundingClientRect().bottom - originTop > tablePage * PITCH + geo.bottom + 0.5) {
    return carryWhole(table, tablePage);
  }
  const headHeight = head ? head.getBoundingClientRect().height : 0;
  const rows = Array.from(table.tBodies).flatMap((b) => Array.from(b.rows));
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const r = row.getBoundingClientRect();
    if (r.height === 0) continue;
    const top = r.top - originTop + (shifted.get(row) ?? 0);
    const bottom = r.bottom - originTop;
    const page = Math.max(0, Math.floor(top / PITCH));
    const start = page * PITCH + geo.top;
    const end = page * PITCH + geo.bottom;
    const runsOver = bottom > end + 0.5;
    const inMargin = top > end + 0.5 || top < start - 0.5;
    if (!runsOver && !inMargin) continue;
    if (i === 0) return carryWhole(table, page);
    /* Taller than a sheet's text, and already at the top of one: it runs on. */
    if (!inMargin && shifted.has(row)) continue;
    const next = top > end + 0.5 || runsOver ? page + 1 : page;
    return {
      el: row,
      row: true,
      target: next * PITCH + geo.top + headHeight,
      page: next,
      ghost: head ? table : null,
    };
  }
  return null;
}

function carryWhole(table: HTMLTableElement, page: number): ReturnType<typeof firstCrossing> {
  const parent = table.parentElement;
  const kids = parent ? (Array.from(parent.children) as HTMLElement[]) : [table];
  return keepWithHeading(kids, kids.indexOf(table), page + 1);
}

/** Push `kids[i]` to the top of `page` — or the heading just above it, so the two stay together. */
function keepWithHeading(kids: HTMLElement[], i: number, page: number): ReturnType<typeof firstCrossing> {
  let at = i;
  while (at > 0 && isHeading(kids[at - 1]!)) at--;
  const el = kids[at]!;
  return { el, row: getComputedStyle(el).display === 'table-row', target: page * PITCH + geo.top, page, ghost: null };
}

function isHeading(el: HTMLElement): boolean {
  return (
    /^H[1-4]$/.test(el.tagName) ||
    el.classList.contains('card__head') ||
    el.classList.contains('works__sub-head') ||
    el.classList.contains('wrep__bridge-head') ||
    el.classList.contains('report__h') ||
    el.classList.contains('sheet__head')
  );
}

/** Whether a box can be broken between its children: a block holding blocks. */
function divisible(el: HTMLElement, cs: CSSStyleDeclaration): boolean {
  if (!['block', 'flow-root', 'list-item'].includes(cs.display)) return false;
  /* Kept whole where the printer keeps it whole. */
  if (cs.breakInside === 'avoid' || cs.breakInside === 'avoid-page') return false;
  if (el.children.length === 0) return false;
  for (const child of Array.from(el.children)) {
    const d = getComputedStyle(child).display;
    if (d.startsWith('inline') || d === 'contents') return false;
  }
  return true;
}

function padTop(row: HTMLElement): number {
  const cell = row.firstElementChild;
  return cell ? parseFloat(getComputedStyle(cell).paddingTop) || 0 : 0;
}

/** A selector naming `el` by its place under `root` — stable for as long as the tree keeps its shape. */
function pathOf(el: Element, root: Element): string {
  const steps: string[] = [];
  let at: Element | null = el;
  while (at && at !== root) {
    const parent: Element | null = at.parentElement;
    if (parent === null) break;
    steps.unshift(`:nth-child(${Array.from(parent.children).indexOf(at) + 1})`);
    at = parent;
  }
  return `.paper__flow > ${steps.join(' > ')}`;
}

/* --------------------------- the sheets --------------------------- */

/** Draw the sheets behind the flow, each with the running head and foot. Redrawn only when they change. */
function drawSheets(paper: HTMLElement, pages: number, ghosts: Ghost[]): void {
  const slot = paper.querySelector<HTMLElement>(`[data-keep="${SHEETS_KEEP}"]`);
  if (slot === null) return;
  const head = paper.querySelector<HTMLElement>('.paper__flow [data-paper-head]');
  const left = paper.dataset['footLeft'] ?? '';
  const mid = paper.dataset['footMid'] ?? '';
  const signature = JSON.stringify([
    pages,
    geo,
    left,
    mid,
    head?.outerHTML ?? '',
    ghosts.map((g) => [g.page, Math.round(g.left), Math.round(g.top), g.table.tHead?.outerHTML ?? '', columnWidths(g.table)]),
  ]);
  if (signature === drawn && slot.childElementCount === pages) return;
  drawn = signature;

  slot.replaceChildren();
  for (let i = 0; i < pages; i++) {
    const page = document.createElement('div');
    page.className = 'paper__sheet';
    page.style.top = `${i * PITCH}px`;
    if (head) {
      const copy = head.cloneNode(true) as HTMLElement;
      copy.removeAttribute('data-paper-head');
      copy.classList.add('paper__head');
      Object.assign(copy.style, { top: `${geo.headTop}px`, left: `${geo.side}px`, right: `${geo.side}px` });
      page.append(copy);
    }
    const foot = document.createElement('div');
    foot.className = 'paper__foot';
    Object.assign(foot.style, { top: `${geo.bottom + 8}px`, left: `${geo.side}px`, right: `${geo.side}px` });
    const cell = (cls: string, text: string) => {
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = text;
      return span;
    };
    foot.append(cell('', left), cell('paper__foot-mid', mid), cell('paper__foot-page', `Page ${i + 1} of ${pages}`));
    page.append(foot);
    slot.append(page);
  }
  for (const g of ghosts) {
    const head = g.table.tHead;
    if (!head) continue;
    const table = g.table.cloneNode(false) as HTMLTableElement;
    table.removeAttribute('id');
    table.classList.add('paper__ghost');
    table.style.left = `${g.left}px`;
    table.style.top = `${g.top}px`;
    table.style.width = `${g.table.getBoundingClientRect().width}px`;
    const copy = head.cloneNode(true) as HTMLTableSectionElement;
    const widths = columnWidths(g.table);
    Array.from(copy.rows[0]?.cells ?? []).forEach((c, j) => {
      c.style.width = `${widths[j] ?? 0}px`;
    });
    table.append(copy);
    slot.append(table);
  }
}

function columnWidths(table: HTMLTableElement): number[] {
  return Array.from(table.tHead?.rows[0]?.cells ?? []).map((c) => Math.round(c.getBoundingClientRect().width));
}

